import { useCallback, useState } from "react";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { Dialog } from "primereact/dialog";
import TabHeader from "../common/TabHeader";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { useSelector } from "react-redux";
import HasRequiredAuthority from "../dependencies/HasRequiredAuthority";
import { AUTHORITIES } from "../../constants";
import { getWriteableDate } from "../../utils";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";

export default function DialogAddBatch({ visible, setBatches, closeDialog }) {
    const { requestAPI, showToast } = useAppContext();
    const { branches = [] } = useSelector((state) => state.stateTemplateConfig?.global);

    const [batch, setBatch] = useState({ active: true });
    const [loading, setLoading] = useState();

    const addBatch = useCallback(() => {
        requestAPI({
            requestPath: `batches`,
            requestMethod: "POST",
            requestPostBody: {
                ...batch,
                start_date: batch?.start_date ? getWriteableDate({ date: batch.start_date, removeTime: true }) : null,
                end_date: batch?.end_date ? getWriteableDate({ date: batch.end_date, removeTime: true }) : null,
            },
            setLoading: setLoading,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Add Batch !", life: 2000 }),
            onResponseReceieved: (created, responseCode) => {
                if (created && responseCode === 201) {
                    showToast({ severity: "success", summary: "Added", detail: "Batch Added", life: 1000 });
                    setBatches((prev) => [created, ...(prev || [])]);
                    setBatch({ active: true });
                    closeDialog();
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Add Batch !", life: 2000 });
                }
            },
        });
    }, [batch, closeDialog, requestAPI, setBatches, showToast]);

    return (
        <Dialog pt={{ content: { className: "overflow-y-auto" } }} header="Add New Batch" visible={visible} className="w-11" onHide={closeDialog}>
            <TabHeader className="pt-3" title="Add New Batch" highlights={["Create a student batch"]} />

            <FloatLabel className="mt-5">
                <InputText
                    value={batch?.title || ""}
                    id="title"
                    className="w-full"
                    onChange={(e) => setBatch((prev) => ({ ...prev, title: e.target.value }))}
                    disabled={loading}
                    pt={{ root: { className: TEXT_NORMAL } }}
                />
                <label htmlFor="title">Title</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <InputTextarea
                    value={batch?.description || ""}
                    id="description"
                    rows={3}
                    className="w-full"
                    onChange={(e) => setBatch((prev) => ({ ...prev, description: e.target.value || null }))}
                    disabled={loading}
                    pt={{ root: { className: TEXT_NORMAL } }}
                />
                <label htmlFor="description">Description</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <Dropdown
                    className="w-full"
                    value={branches?.find(({ id }) => id === batch?.branch_id)}
                    inputId="branch"
                    options={branches}
                    optionLabel="title"
                    showClear
                    onChange={(e) => setBatch((prev) => ({ ...prev, branch_id: e.value?.id || null }))}
                    disabled={loading}
                    pt={{ input: { className: TEXT_NORMAL }, item: { className: TEXT_NORMAL } }}
                />
                <label htmlFor="branch">Branch</label>
            </FloatLabel>

            <div className="flex gap-2 mt-5">
                <FloatLabel className="flex-1">
                    <Calendar
                        className="w-full"
                        inputId="start_date"
                        value={batch?.start_date}
                        onChange={(e) => setBatch((prev) => ({ ...prev, start_date: e.value }))}
                        disabled={loading}
                        dateFormat="dd/mm/yy"
                    />
                    <label htmlFor="start_date" className={TEXT_SMALL}>
                        Start Date
                    </label>
                </FloatLabel>
                <FloatLabel className="flex-1">
                    <Calendar
                        className="w-full"
                        inputId="end_date"
                        value={batch?.end_date}
                        onChange={(e) => setBatch((prev) => ({ ...prev, end_date: e.value }))}
                        disabled={loading}
                        dateFormat="dd/mm/yy"
                        minDate={batch?.start_date}
                    />
                    <label htmlFor="end_date" className={TEXT_SMALL}>
                        End Date
                    </label>
                </FloatLabel>
            </div>

            <HasRequiredAuthority requiredAuthority={AUTHORITIES.CREATE_BATCH}>
                <Button className="mt-3" label="Add Batch" severity="warning" loading={loading} onClick={addBatch} disabled={!batch?.title} />
            </HasRequiredAuthority>
        </Dialog>
    );
}
